import { ApiError } from "@chai/api-client";
import { Wordmark } from "@chai/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";

import { useApi } from "../api";
import { useAuth } from "../auth";
import { useDocumentTitle } from "@/lib/document-title";

export const Route = createFileRoute("/invite/$token")({
  component: InvitePreviewPage,
});

interface InvitePreview {
  org_name?: string;
  email?: string;
  role?: string;
  inviter_name?: string;
}

function InvitePreviewPage() {
  const api = useApi();
  const auth = useAuth();
  const navigate = useNavigate();
  const { token } = useParams({ from: "/invite/$token" });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const previewQuery = useQuery({
    queryKey: ["invite-preview", token],
    queryFn: () => api.request<InvitePreview>(`/invitations/${token}`),
  });

  const orgName = previewQuery.data?.org_name;
  useDocumentTitle([orgName ? `Invitation to ${orgName}` : "Invitation"]);

  const accept = async () => {
    setError(null);
    setPending(true);
    try {
      await api.request(`/invitations/${token}/accept`, { method: "POST" });
      await auth.refresh();
      void navigate({ to: "/workspaces" });
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError((err as Error).message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="min-h-dvh bg-background">
      <header className="px-6 py-5">
        <Wordmark />
      </header>
      <main className="mx-auto flex max-w-[480px] flex-col gap-6 px-6 pb-16">
        <header className="pt-6 flex flex-col gap-1">
          <p className="meta-strip">Invitation</p>
          <h1 className="text-3xl font-semibold tracking-tight">You've been invited</h1>
        </header>
        {previewQuery.isLoading ? (
          <Skeleton className="h-44 w-full rounded-lg" />
        ) : previewQuery.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Invitation unavailable</AlertTitle>
            <AlertDescription>
              We couldn't load this invitation. Ask the inviter to send a fresh link.
            </AlertDescription>
          </Alert>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Join {previewQuery.data?.org_name ?? "the team"}</CardTitle>
              <CardDescription>
                {previewQuery.data?.inviter_name
                  ? `${previewQuery.data.inviter_name} invited you`
                  : "You've been invited to join this org."}
                {previewQuery.data?.role && (
                  <>
                    {" "}
                    as a <span className="font-mono text-xs">{previewQuery.data.role}</span>.
                  </>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive">
                  <AlertTitle>Could not accept invitation</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </CardContent>
            <CardFooter className="flex flex-wrap justify-between gap-2">
              {auth.me ? (
                <Button onClick={accept} disabled={pending}>
                  {pending && <Spinner data-icon="inline-start" />}
                  Accept invitation
                </Button>
              ) : (
                <Button
                  onClick={() =>
                    void navigate({
                      to: "/signup",
                      search: { token },
                    })
                  }
                >
                  Create account & accept
                </Button>
              )}
              {!auth.me && (
                <Button render={<Link to="/signin" />} variant="ghost">
                  Sign in instead
                </Button>
              )}
            </CardFooter>
          </Card>
        )}
      </main>
    </div>
  );
}
